import React, { useState, useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

interface DrainText {
  angle: number;
  dist: number;
  age: number;
  life: number;
}

export function OnionLayers() {
  const [selectedLayer, setSelectedLayer] = useState<{
    name: string;
    fusion: string;
    energy: string;
    detail: string;
  } | null>(null);

  const drainTextsRef = useRef<DrainText[]>([]);
  const lastSpawnRef = useRef<number>(0);

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;

    const cx = width / 2;
    const cy = height / 2;

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // 1. UPDATE DRAIN TEXTS
    // Spawn every 1.5 seconds (synchronised with pulse)
    if (totalTime - lastSpawnRef.current >= 1.5) {
      lastSpawnRef.current = totalTime;
      for (let i = 0; i < 3; i++) {
        drainTextsRef.current.push({
          angle: Math.random() * Math.PI * 2,
          dist: 20,
          age: 0,
          life: 1.2
        });
      }
    }

    // 2. DRAW STAR ONION LAYERS (Outermost first)
    const layers = [
      { name: 'Hydrogen Shell', r: 150, fill: 'rgba(147, 197, 253, 0.22)', label: 'H → He', color: '#93C5FD' },
      { name: 'Helium Shell', r: 120, fill: 'rgba(167, 243, 208, 0.22)', label: 'He → C', color: '#A7F3D0' },
      { name: 'Carbon Shell', r: 95, fill: 'rgba(253, 230, 138, 0.22)', label: 'C → Ne', color: '#FDE68A' },
      { name: 'Neon Shell', r: 73, fill: 'rgba(252, 211, 77, 0.26)', label: 'Ne → O', color: '#FCD34D' },
      { name: 'Oxygen Shell', r: 54, fill: 'rgba(249, 115, 22, 0.32)', label: 'O → Si', color: '#F97316' },
      { name: 'Silicon Shell', r: 37, fill: 'rgba(239, 68, 68, 0.38)', label: 'Si → Fe', color: '#EF4444' }
    ];

    layers.forEach((layer) => {
      // Draw shell filled circle
      ctx.fillStyle = layer.fill;
      ctx.beginPath();
      ctx.arc(cx, cy, layer.r, 0, Math.PI * 2);
      ctx.fill();

      // Draw shell border
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw label horizontally inside right edge of layer
      ctx.fillStyle = layer.color;
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(layer.label, cx + layer.r - 8, cy - 3);
    });

    // 3. DRAW IRON CORE (Pulsing)
    const corePulse = Math.sin(totalTime * 4.0) * 2.5; // oscillate ±2.5px
    const coreRadius = 20 + corePulse;

    ctx.fillStyle = '#DC2626'; // Bright red core
    ctx.beginPath();
    ctx.arc(cx, cy, coreRadius, 0, Math.PI * 2);
    ctx.fill();

    // Core label
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 8px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Fe CORE', cx, cy + 3);

    // 4. DRAW IRON CORE ENERGY DRAIN SHOCKWAVE (Every 1.5s)
    const waveCycle = totalTime % 1.5;
    const waveProgress = waveCycle / 1.5; // 0 to 1
    const waveRadius = 20 + waveProgress * 130; // 20 to 150
    const waveOpacity = 1 - waveProgress;

    ctx.strokeStyle = `rgba(220, 38, 38, ${waveOpacity * 0.8})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, waveRadius, 0, Math.PI * 2);
    ctx.stroke();

    // 5. UPDATE AND DRAW DRAIN TEXTS
    drainTextsRef.current = drainTextsRef.current.filter((t) => {
      t.age += deltaTime;
      if (t.age >= t.life) return false;

      t.dist += deltaTime * 55; // speed 55px/s
      const opacity = 1 - t.age / t.life;
      const tx = cx + Math.cos(t.angle) * t.dist;
      const ty = cy + Math.sin(t.angle) * t.dist;

      ctx.fillStyle = `rgba(248, 113, 113, ${opacity})`;
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('-Energy', tx, ty);
      return true;
    });

    // 6. DRAW SELECTION HIGHLIGHT RING
    if (selectedLayer) {
      const rMap: Record<string, number> = {
        'Iron Core': 20,
        'Silicon Shell': 37,
        'Oxygen Shell': 54,
        'Neon Shell': 73,
        'Carbon Shell': 95,
        'Helium Shell': 120,
        'Hydrogen Shell': 150
      };
      const r = rMap[selectedLayer.name];
      if (r) {
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2.5;
        ctx.shadowColor = '#FFFFFF';
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.shadowBlur = 0; // reset
      }
    }

    // Top-Left Header Labels
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Star Onion Layers', 16, 26);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.font = '10px sans-serif';
    ctx.fillText('Tap any shell to inspect fusion stages', 16, 42);
  }, [selectedLayer]);

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const cx = width / 2;
    const cy = height / 2;

    const dist = Math.sqrt(Math.pow(x - cx, 2) + Math.pow(y - cy, 2));

    // Selection checking based on radius boundaries
    if (dist <= 23) {
      setSelectedLayer({
        name: 'Iron Core',
        fusion: 'No Fusion (Absorbs Energy)',
        energy: 'NONE — absorbs energy!',
        detail: '☠ Iron cannot be fused. No energy released. Collapse imminent.'
      });
    } else if (dist <= 37) {
      setSelectedLayer({
        name: 'Silicon Shell',
        fusion: 'Silicon fuses to Iron (Si → Fe)',
        energy: 'Energy released',
        detail: 'The final exothermic nuclear stage in the life of a massive star.'
      });
    } else if (dist <= 54) {
      setSelectedLayer({
        name: 'Oxygen Shell',
        fusion: 'Oxygen fuses to Silicon (O → Si)',
        energy: 'Medium-high energy released',
        detail: 'Fuses oxygen nuclei to synthesize silicon, neon, and phosphorus.'
      });
    } else if (dist <= 73) {
      setSelectedLayer({
        name: 'Neon Shell',
        fusion: 'Neon fuses to Oxygen (Ne → O)',
        energy: 'Medium energy released',
        detail: 'Neon is photodisintegrated, recombining into oxygen and magnesium.'
      });
    } else if (dist <= 95) {
      setSelectedLayer({
        name: 'Carbon Shell',
        fusion: 'Carbon fuses to Neon (C → Ne)',
        energy: 'High energy released',
        detail: 'Fuses carbon nuclei at roughly 500 million Kelvin.'
      });
    } else if (dist <= 120) {
      setSelectedLayer({
        name: 'Helium Shell',
        fusion: 'Helium fuses to Carbon (He → C)',
        energy: 'Very high energy released',
        detail: 'Triple-alpha process fusion taking place inside the outer envelope.'
      });
    } else if (dist <= 150) {
      setSelectedLayer({
        name: 'Hydrogen Shell',
        fusion: 'Hydrogen fuses to Helium (H → He)',
        energy: 'Highest energy output',
        detail: 'Outermost shell fusing hydrogen via the CNO cycle.'
      });
    } else {
      setSelectedLayer(null);
    }
  };

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} onClick={handleCanvasClick} className="block w-full h-full cursor-pointer" />

      {/* Selected Info Overlay (Absolute Positioned Panel) */}
      {selectedLayer && (
        <div className="absolute top-3 right-3 w-[210px] bg-[#0b0f19]/90 backdrop-blur-md p-3 rounded-lg border border-white/15 shadow-xl flex flex-col gap-1.5 animation-fade-in text-white">
          <div className="flex justify-between items-center border-b border-white/10 pb-1">
            <span className="font-bold text-xs text-cyan-400">{selectedLayer.name}</span>
            <button
              onClick={() => setSelectedLayer(null)}
              className="text-white/50 hover:text-white text-xs font-bold leading-none p-0.5"
            >
              ✕
            </button>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] uppercase tracking-wider text-white/40">Fusion Process</span>
            <span className="text-xs font-semibold">{selectedLayer.fusion}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] uppercase tracking-wider text-white/40">Energy Status</span>
            <span className={`text-xs font-bold ${selectedLayer.name === 'Iron Core' ? 'text-red-400' : 'text-emerald-400'}`}>
              {selectedLayer.energy}
            </span>
          </div>
          <p className="text-[10px] leading-relaxed text-white/70 italic mt-0.5">
            {selectedLayer.detail}
          </p>
        </div>
      )}
    </div>
  );
}
